/**
 * Achievement-unlock celebration. Fires once per achievement per user when
 * the gamification query reports `newlyUnlocked` ids (server-tracked seen
 * set, so it's once per user, not per device). Same provider shape as
 * WelcomeModal / AnnouncementModal: auto-fire off a query, dismiss → POST
 * seen → invalidate.
 *
 * The DrillView refetches `["gamification"]` after each turn, so crossing a
 * threshold mid-session pops this without a reload. A local shown-set ref
 * guards the race between dismiss and the refetch that clears
 * `newlyUnlocked`.
 *
 * Mounted in App.tsx between the daily-challenge celebration and the
 * announcement modal.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getGamification,
  markAchievementsSeen,
  type Achievement,
} from "@/features/gamification/lib/gamification";
import { AchievementIcon } from "@/features/gamification/lib/achievementIcons";
import { getBillingStatus } from "@/features/billing/lib/billing";
import { useAuth } from "@/features/auth/lib/authContext";
import { ICON_STROKE, X } from "@/shared/components/icons";
import { LogoMark } from "@/shared/components/LogoMark";

export function AchievementUnlockProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const signedIn = authStatus.kind === "signedIn";
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["gamification"],
    queryFn: getGamification,
    enabled: signedIn,
    staleTime: 60_000,
  });
  // During first-report onboarding the nudge modals are the ONLY pop-ups —
  // achievement unlocks are silenced here (still earned, still on /skills/
  // level) so the new user has one clear goal. Resumes after the report.
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    enabled: signedIn,
    staleTime: 60_000,
  });

  const [shown, setShown] = useState<Achievement[]>([]);
  const [open, setOpen] = useState(false);
  // Ids already shown this session — guards the dismiss→refetch race where
  // `newlyUnlocked` still carries the just-dismissed ids for a beat.
  const shownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!signedIn || open || !data || !billing) return;
    const freshIds = data.newlyUnlocked.filter(
      (id) => !shownIdsRef.current.has(id),
    );
    if (freshIds.length === 0) return;

    const preReport =
      billing.subscriptionStatus === "free" &&
      billing.signalCount < billing.signalsForFirstReport &&
      !billing.firstLeakReportMilestoneShown;
    if (preReport) {
      // Silently mark seen so they don't pile up to all pop at once post-AHA.
      freshIds.forEach((id) => shownIdsRef.current.add(id));
      markAchievementsSeen(freshIds).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ["gamification"] });
      return;
    }

    const freshSet = new Set(freshIds);
    const achievements = data.achievements.filter((a) => freshSet.has(a.id));
    if (achievements.length === 0) return;
    setShown(achievements);
    setOpen(true);
  }, [signedIn, data, billing, open, queryClient]);

  const dismiss = async () => {
    const ids = shown.map((a) => a.id);
    ids.forEach((id) => shownIdsRef.current.add(id));
    setOpen(false);
    try {
      await markAchievementsSeen(ids);
    } catch {
      // Silent — server seen-set will catch up; re-fires next session at worst.
    }
    queryClient.invalidateQueries({ queryKey: ["gamification"] });
  };

  return (
    <>
      {children}
      {open && shown.length > 0 && (
        <AchievementUnlockView achievements={shown} onDismiss={dismiss} />
      )}
    </>
  );
}

function AchievementUnlockView({
  achievements,
  onDismiss,
}: {
  achievements: Achievement[];
  onDismiss: () => void;
}) {
  const multiple = achievements.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-2xl border border-accent/40 bg-surface relative shadow-[0_28px_60px_-20px_rgba(124,147,241,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 pt-8 space-y-4">
          <div className="mx-auto w-12 h-12 flex items-center justify-center">
            <LogoMark size={44} animate />
          </div>
          <h2 className="text-xl font-bold text-text tracking-tight text-center">
            {multiple ? "Achievements unlocked" : "Achievement unlocked"}
          </h2>

          <div className="space-y-2.5">
            {achievements.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 rounded-xl border border-accent/30 bg-accent/10 p-3"
              >
                <div className="w-10 h-10 shrink-0 rounded-lg bg-accent/20 text-accent flex items-center justify-center">
                  <AchievementIcon name={a.icon} size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-text">{a.title}</div>
                  <div className="text-xs text-text-muted leading-snug">
                    {a.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pt-2 pb-5 border-t border-border/40">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition"
          >
            Nice
          </button>
        </div>
      </div>
    </div>
  );
}
